import React, { useCallback, useState } from 'react';
import axios from 'axios';
import Header from '../index/components/Header/Header';
import ProgressBar from '../Cart/components/ProgressBar/ProgressBar';
import ShippingAddress from './components/ShippingAddress/ShippingAddress';
import DeliveryOptions from './components/DeliveryOptions/DeliveryOptions';
import OrderSummary from './components/OrderSummary/OrderSummary';
import { ApiService } from '../../shared/service/Apiservice';
import { AddressService } from '../Profile/service/Address.service';
import './styles/Shipping.css';

import { useLocation, useNavigate } from 'react-router-dom';
import { environment } from '../../shared/enviroment/environment';
import CheckoutService from '../Checkout/service/Checkout.service';
import Spinner from '@/shared/components/Spinner/Spinner';

const Shipping: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const checkoutState = location.state || {};
  const [selectedPayment, setSelectedPayment] = useState<'online' | 'cod' | null>(null);
  const [selectedDelivery, setSelectedDelivery] = useState<'immediate' | 'schedule' | null>(null);
  const [deliveryDetails, setDeliveryDetails] = useState<{ deliveryType: 'immediate' | 'schedule'; scheduledDeliveryDate?: string; scheduledDeliveryTime?: string }>({ deliveryType: 'immediate' });
  const [newAddressData, setNewAddressData] = useState<any>(null);
  const [shouldSaveAddress, setShouldSaveAddress] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [currentCartItems, setCurrentCartItems] = useState<any[]>(checkoutState.cartItems || []);
  const [currentSubTotal, setCurrentSubTotal] = useState<number>(checkoutState.subTotal || 0);
  const [currentGrandTotal, setCurrentGrandTotal] = useState<number>(checkoutState.grandTotal || 0);
  const [finalOrderTotal, setFinalOrderTotal] = useState<number>(checkoutState.grandTotal || 0);

  const handlePaymentSelect = (paymentType: 'online' | 'cod' | null) => {
    setSelectedPayment(paymentType);
  };

  const handleDeliverySelect = useCallback((deliveryType: 'immediate' | 'schedule' | null) => {
    setSelectedDelivery(deliveryType);
  }, []);

  const handleDeliveryDetailsChange = useCallback((details: { deliveryType: 'immediate' | 'schedule'; scheduledDeliveryDate?: string; scheduledDeliveryTime?: string }) => {
    setDeliveryDetails(details);
  }, []);

  const handleAddressChange = (address: any, shouldSave: boolean) => {
    setNewAddressData(address);
    setShouldSaveAddress(shouldSave);
  };

  // Handler for quantity changes in the order summary
  const handleQuantityChange = useCallback((itemId: string | number, newQuantity: number) => {
    setCurrentCartItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        const currentItemId = item.id || item.productId;
        if (currentItemId === itemId) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      
      // Recalculate totals
      const newSubTotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const deliveryFee = checkoutState.deliveryFee || 99;
      const newGrandTotal = newSubTotal + deliveryFee;
      
      setCurrentSubTotal(newSubTotal);
      setCurrentGrandTotal(newGrandTotal);
      
      return updatedItems;
    });
  }, [checkoutState.deliveryFee]);

  // Handler to receive the final total from OrderSummary (after discounts, build coins, etc.)
  const handleFinalTotalChange = useCallback((total: number) => {
    setFinalOrderTotal(total);
  }, []);

  const saveAddressAfterOrder = async () => {
    if (shouldSaveAddress && newAddressData) {
      try {
        const apiService = new ApiService(axios.create());
        const addressService = new AddressService(apiService);
        
        const addressPayload = {
          label: 'Other' as const,
          name: newAddressData.fullName,
          phone_number: newAddressData.phone,
          primary_address_line: newAddressData.street,
          city: newAddressData.city,
          state: newAddressData.state,
          pincode: newAddressData.pincode,
          is_add_usecase: true,
        };

        await addressService.addOrEditAddress(addressPayload);
      } catch (error) {
        console.error('Failed to save address:', error);
      }
    }
  };

  // Place order and handle payment

  const handleOrderPlacement = async () => {
    if (placingOrder) return;
    setPlacingOrder(true);
    // Use current cart items state instead of initial checkout state
    const cartItems = currentCartItems;
    const subTotal = currentSubTotal;
    const deliveryFee = checkoutState.deliveryFee || 99;
    const grandTotal = finalOrderTotal; // Use final total with discounts applied
    const address = newAddressData || {};
    const paymentType = selectedPayment;
    const deliveryType = selectedDelivery;

    // Basic validations aligned with reference logic
    if (!paymentType) {
      alert('Please select a payment method');
      setPlacingOrder(false);
      return;
    }
    if (!deliveryType) {
      alert('Please select a delivery option');
      setPlacingOrder(false);
      return;
    }
    if (!address?.fullName || !address?.phone || !address?.primary_address_line || !address?.locality) {
      alert('Please complete shipping address details');
      setPlacingOrder(false);
      return;
    }
    if (deliveryDetails.deliveryType === 'schedule') {
      if (!deliveryDetails.scheduledDeliveryDate) {
        alert('Please scroll down and select a delivery date');
        setPlacingOrder(false);
        return;
      }
      if (!deliveryDetails.scheduledDeliveryTime) {
        alert('Please scroll down and select a delivery time slot');
        setPlacingOrder(false);
        return;
      }
    }

    // Prepare order payload
    const mappedItems = (cartItems || [])
      .map((item: any) => {
        const productId = item.productId || (item.id ? item.id.replace(/-$/, '') : undefined);
        if (!productId) return null;
        return {
          product: productId,
          quantity: item.quantity,
          price: item.price,
          variantId: item.variantId || item.variantSku,
          variantSku: item.variantSku || item.variantId,
          variantTitle: item.variantTitle,
          variantImage: item.variantImage,
          name: item.name,
          image: item.image || '',
          variantInfo: item.variantInfo || {
            name: item.name,
            sku: item.variantSku || item.variantId,
            price: item.price,
            quantity: item.quantity,
            weight: item.weight || 0,
            weightUnit: item.weightUnit || 'kg',
            isDefault: true,
            variantTitle: item.variantTitle,
            variantImage: item.variantImage,
            image: item.image,
          },
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    const orderPayload = {
      items: mappedItems,
      subTotal,
      shippingCost: deliveryFee,
      totalAmount: grandTotal,
      deliveryFee,
      grandTotal,
      shippingDetails: {
        address: {
          primary_address_line: address.primary_address_line,
          secondary_address_line: address.secondary_address_line,
          locality: address.locality,
          landmark: address.landmark,
        },
        fullName: address.fullName,
        phone: address.phone,
        location: {
          latitude: (address as any).latitude,
          longitude: (address as any).longitude,
        },
      },
      paymentMode: paymentType === 'online' ? 'Razorpay' : 'COD',
      deliveryType: deliveryDetails.deliveryType === 'schedule' ? 'scheduled' : 'immediate',
      scheduledDeliveryDate: deliveryDetails.scheduledDeliveryDate,
      scheduledDeliveryTime: deliveryDetails.scheduledDeliveryTime,
    };

    const checkoutService = new CheckoutService();

    if (paymentType === 'online') {
      try {
        // Create Razorpay order
        const razorpayOrder = await checkoutService.createOrder({
          items: mappedItems,
          currency: 'INR',
          receipt: `order_${Date.now()}`,
          couponCode: checkoutState?.appliedCoupon || undefined,
          paymentMode: 'Razorpay',
        });

        // Support both response shapes: { id, amount, currency, key_id } or { order: { id, amount, currency }, key_id }
        const orderObj = (razorpayOrder && razorpayOrder.order) ? razorpayOrder.order : razorpayOrder;
        const keyId = razorpayOrder?.key_id || environment.razorpay.keyId;

        if (!orderObj?.id) {
          alert('Failed to create Razorpay order');
          setPlacingOrder(false);
          return;
        }

        // Load Razorpay SDK if needed
        const ensureRzp = () => new Promise<boolean>((resolve) => {
          if (window.Razorpay) return resolve(true);
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = true;
          script.onload = () => resolve(true);
          script.onerror = () => resolve(false);
          document.body.appendChild(script);
        });

        const loaded = await ensureRzp();
        if (!loaded) {
          alert('Failed to load payment processor');
          setPlacingOrder(false);
          return;
        }

        const options = {
          key: keyId,
          amount: orderObj.amount,
          currency: orderObj.currency,
          name: environment.app.name,
          description: 'Order Payment',
          order_id: orderObj.id,
          handler: async (response: any) => {
            try {
              // Verify payment
              const verification = await checkoutService.verifyPayment({
                razorpay_order_id: orderObj.id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_id: orderObj.id,
                shipping_details: {
                  fullName: address.fullName,
                  phone: address.phone,
                  address: {
                    primary_address_line: address.primary_address_line,
                    secondary_address_line: address.secondary_address_line,
                    locality: address.locality,
                    landmark: address.landmark,
                  },
                },
                items: mappedItems,
                totalAmount: grandTotal,
                paymentMode: 'Razorpay',
              });

              if (!verification) {
                alert('Payment verification failed');
                setPlacingOrder(false);
                return;
              }

              // Create order record only after verification
              const orderRecordPayload = {
                ...orderPayload,
                paymentId: response.razorpay_payment_id,
                razorpayOrderId: response.razorpay_order_id,
                razorpaySignature: response.razorpay_signature,
              };

              const orderResponse = await checkoutService.createOrderRecord(orderRecordPayload);
              const orderId = orderResponse?.data?.order?._id || orderResponse?.order?._id;

              // Save address after successful order if opted
              await saveAddressAfterOrder();
              
              // Redirect to order confirmation page with order ID in state
              navigate('/order-confirmation', { 
                state: { _id: orderId } 
              });
            } catch (err) {
              console.error(err);
              alert('Order creation failed after payment. Please contact support.');
            } finally {
              setPlacingOrder(false);
            }
          },
          prefill: {
            name: address.fullName,
            contact: address.phone,
          },
          theme: { color: '#528FF0' },
          modal: {
            ondismiss: () => setPlacingOrder(false),
          },
        } as any;

        const rzp = new window.Razorpay(options);
        try {
          rzp.open();
          // Mark confirmation step as active when Razorpay opens successfully
          sessionStorage.setItem('progressStepOverride', '2');
          window.dispatchEvent(new Event('progressStepChange'));
        } catch (openErr) {
          console.error('Failed to open Razorpay:', openErr);
          // Ensure we stay on Shipping step if open fails
          sessionStorage.setItem('progressStepOverride', '1');
          window.dispatchEvent(new Event('progressStepChange'));
        }

        // If user closes the modal without paying, revert back to Shipping step
        rzp.on('modal.closed', () => {
          sessionStorage.setItem('progressStepOverride', '1');
          window.dispatchEvent(new Event('progressStepChange'));
        });

        // On payment failure, keep Shipping step
        rzp.on('payment.failed', () => {
          sessionStorage.setItem('progressStepOverride', '1');
          window.dispatchEvent(new Event('progressStepChange'));
        });
      } catch (e) {
        console.error(e);
        alert('Failed to process payment. Please try again.');
        setPlacingOrder(false);
      }
    } else if (paymentType === 'cod') {
      try {
        // Directly create order for COD
        const codOrderResponse = await checkoutService.createOrderRecord(orderPayload);
        const codOrderId = codOrderResponse?.data?.order?._id || codOrderResponse?.order?._id;
        
        // Save address if needed
        await saveAddressAfterOrder();
        
        // Move progress to Confirmation on successful COD order
        sessionStorage.setItem('progressStepOverride', '2');
        window.dispatchEvent(new Event('progressStepChange'));
        
        // Redirect to order confirmation page with order ID
        navigate('/order-confirmation', { 
          state: { _id: codOrderId } 
        });
      } catch (e) {
        console.error(e);
        alert('Failed to place COD order. Please try again.');
      } finally {
        setPlacingOrder(false);
      }
    }
  };

  return (
    <div className="shipping-page">
      {placingOrder && <Spinner />}
      <Header />
      <ProgressBar />
    <div className="shipping-content">
      <div className="shipping-left">
        <ShippingAddress 
          onPaymentSelect={handlePaymentSelect} 
          onAddressChange={handleAddressChange}
        />
        {selectedPayment && (
          <DeliveryOptions 
            onDeliverySelect={handleDeliverySelect} 
            onDeliveryDetailsChange={handleDeliveryDetailsChange}
          />
        )}
      </div>
      <div className="shipping-right">
        <OrderSummary
          items={currentCartItems}
          subTotal={currentSubTotal}
          delivery={checkoutState.deliveryFee || 99}
          grandTotal={currentGrandTotal}
          onQuantityChange={handleQuantityChange}
          onFinalTotalChange={handleFinalTotalChange}
          onOrderPlaced={handleOrderPlacement}
          selectedPayment={selectedPayment}
          selectedDelivery={selectedDelivery}
        />
      </div>
    </div>
  </div>
  );
};

export default Shipping;